import { Component } from '@theme/component';

const TRIGGER_HASH = '#schedule-call';

/** Stylesheets this component adopted into a Shopify Forms shadow root, so re-renders replace them. */
const skinSheets = new WeakSet();

/**
 * "Schedule a Call" dialog for sections/arvana-schedule-call.liquid.
 * Opens from any link whose URL ends in #schedule-call, walks through two steps (details, then date
 * and time) and submits Shopify's contact form natively, so the server-rendered success or error
 * state reopens the dialog after the page reloads. With a Shopify Forms app block, only the
 * dialog lifecycle is managed here; Forms owns its fields, validation, submission and success.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal dialog.
 * @property {HTMLFormElement} form - The contact form inside the dialog.
 * @property {HTMLElement[]} steps - Step panels: details, then date and time.
 * @property {HTMLElement} success - Confirmation panel shown after a successful submit.
 * @property {HTMLElement} [successHeading] - Heading of the confirmation panel.
 * @property {HTMLElement} [nativeHeading] - Dialog heading in Shopify Forms mode.
 * @property {HTMLTemplateElement} [formsSkin] - Arvana CSS for the Shopify Forms shadow root.
 * @property {HTMLElement} detailFields - Wrapper of the step 1 fields.
 * @property {HTMLInputElement} dateInput - Hidden `contact[Call date]` field.
 * @property {HTMLInputElement} timeInput - Hidden `contact[Call time]` field.
 * @property {HTMLElement} monthLabel - Month and year above the calendar.
 * @property {HTMLElement} dateLabel - Selected date above the time slots.
 * @property {HTMLButtonElement} prevMonth - Previous month button.
 * @property {HTMLButtonElement} nextMonth - Next month button.
 * @property {HTMLTableSectionElement} weekdays - Calendar head.
 * @property {HTMLTableSectionElement} days - Calendar body, rendered here.
 * @property {HTMLInputElement[]} [slots] - Time slot radios.
 * @property {HTMLElement} pickerError - Message shown when the date or time is missing.
 * @property {HTMLInputElement} [openOnLoad] - Present when the server rendered a result for this form.
 *
 * @extends {Component<Refs>}
 */
class ArvanaScheduleCallComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** Index of the visible step, or -1 for the confirmation panel. */
  #step = 0;

  /** First day of the month shown in the calendar. */
  #viewMonth = startOfMonth(today());

  /** @type {Date | null} */
  #selectedDate = null;

  /** Day that holds the calendar's roving tab stop. @type {Date | null} */
  #focusDate = null;

  /** @type {Element | null} */
  #trigger = null;

  /** Re-adopts the Shopify Forms skin after the embed re-renders. @type {MutationObserver | undefined} */
  #skinObserver;

  /** Polls for the hCaptcha challenge Forms may show on submit. @type {number | undefined} */
  #captchaTimer;

  /** True while the dialog closes only to reopen in the other mode, so the close isn't handled. */
  #switchingMode = false;

  connectedCallback() {
    this.#configureRequiredRefs();
    super.connectedCallback();
    this.#skinShopifyForms();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('click', this.#handleTriggerClick, { signal });
    window.addEventListener('hashchange', this.#handleHashChange, { signal });
    document.addEventListener('shopify:section:select', this.#handleSectionSelect, { signal });
    document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect, { signal });
    // `submit` bubbles to the component; `close` does not, so listen in the capture phase
    this.addEventListener('submit', this.#handleSubmit, { signal });
    this.addEventListener('close', this.#handleClose, { signal, capture: true });
    this.addEventListener('input', this.#handleInput, { signal });

    this.#setup();

    if (this.refs.openOnLoad || window.location.hash === TRIGGER_HASH) this.open();
  }

  updatedCallback() {
    this.#configureRequiredRefs();
    super.updatedCallback();
    this.#skinShopifyForms();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#skinObserver?.disconnect();
    clearInterval(this.#captchaTimer);
  }

  /** Opens the dialog on the current step. */
  open() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#trigger = document.activeElement;
    dialog.showModal();
    document.dispatchEvent(new CustomEvent('arvana:schedule-call:open'));

    if (this.dataset.submissionProvider === 'shopify-forms') {
      this.refs.nativeHeading?.focus();
      this.#watchCaptcha();
      return;
    }

    if (this.refs.openOnLoad && !this.refs.success.hidden) {
      this.#showPanel(-1);
    } else {
      this.#showPanel(this.#step, { focusField: true });
    }
  }

  /** Closes the dialog (`on:click="/close"`). */
  close() {
    this.refs.dialog.close();
  }

  /**
   * Closes the dialog when the click lands on the backdrop (`on:click="/handleDialogClick"`).
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    const { dialog } = this.refs;
    // Keyboard-activated clicks report detail 0 and no pointer position
    if (event.detail === 0 || event.composedPath()[0] !== dialog) return;

    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!inside) this.close();
  }

  /** Validates step 1 and moves to the date and time step (`on:click="/next"`). */
  next() {
    const fields = this.refs.detailFields.querySelectorAll('input, select, textarea');
    /** @type {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null} */
    let firstInvalid = null;

    for (const field of fields) {
      if (
        !(field instanceof HTMLInputElement) &&
        !(field instanceof HTMLSelectElement) &&
        !(field instanceof HTMLTextAreaElement)
      ) {
        continue;
      }

      const valid = field.checkValidity();
      if (valid) {
        field.removeAttribute('aria-invalid');
      } else {
        field.setAttribute('aria-invalid', 'true');
        firstInvalid ??= field;
      }
    }

    if (firstInvalid) {
      firstInvalid.focus();
      firstInvalid.reportValidity();
      return;
    }

    if (!this.#selectedDate) {
      const first = this.#firstAvailableDate();
      if (first) this.#selectDate(first);
    }

    this.#showPanel(1);
  }

  /** Returns to step 1 (`on:click="/back"`). */
  back() {
    this.#showPanel(0);
  }

  /** `on:click="/previousMonth"` */
  previousMonth() {
    this.#changeMonth(-1);
  }

  /** `on:click="/nextMonth"` */
  nextMonth() {
    this.#changeMonth(1);
  }

  /**
   * Selects the clicked day (`on:click="/selectDay"` on the calendar body).
   * @param {MouseEvent} event
   */
  selectDay(event) {
    const button = this.#dayButtonFrom(event);
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    const date = parseIsoDate(button.dataset.date ?? '');
    if (date) this.#selectDate(date);
  }

  /**
   * Arrow, Home/End and PageUp/PageDown navigation between days (`on:keydown="/navigateDays"`).
   * @param {KeyboardEvent} event
   */
  navigateDays(event) {
    const button = this.#dayButtonFrom(event);
    const current = button ? parseIsoDate(button.dataset.date ?? '') : null;
    if (!current) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    let target;
    switch (event.key) {
      case forward:
        target = addDays(current, 1);
        break;
      case backward:
        target = addDays(current, -1);
        break;
      case 'ArrowDown':
        target = addDays(current, 7);
        break;
      case 'ArrowUp':
        target = addDays(current, -7);
        break;
      case 'Home':
        target = addDays(current, -current.getDay());
        break;
      case 'End':
        target = addDays(current, 6 - current.getDay());
        break;
      case 'PageUp':
        target = addMonths(current, -1);
        break;
      case 'PageDown':
        target = addMonths(current, 1);
        break;
      default:
        return;
    }

    event.preventDefault();

    const { min, max } = this.#range();
    if (target < startOfMonth(min) || target > max) return;
    if (target < min) target = min;

    this.#focusDate = target;
    this.#viewMonth = startOfMonth(target);
    this.#renderMonth();
    this.#focusDay(target);
  }

  /**
   * Stores the chosen time slot (`on:change="/selectTime"`).
   * @param {Event} event
   */
  selectTime(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;

    const zone = this.dataset.timeZone ?? '';
    this.refs.timeInput.value = [input.value, zone].filter(Boolean).join(' ');
    this.refs.pickerError.hidden = true;
  }

  /** Builds the calendar for the current settings. Safe to call again after a re-render. */
  #setup() {
    if (this.dataset.submissionProvider === 'shopify-forms') return;

    this.#renderWeekdays();
    this.#renderMonth();
    this.#updateSlots();
  }

  /**
   * Adopts the Arvana skin into the Shopify Forms embed's shadow root, which theme CSS can't reach.
   * Forms inserts `<shopify-forms-embed>` after load and pushes its own stylesheets as it renders,
   * so watch for the embed and keep the skin last after every render.
   */
  #skinShopifyForms() {
    const { formsSkin } = this.refs;
    this.#skinObserver?.disconnect();
    if (this.dataset.submissionProvider !== 'shopify-forms' || !formsSkin) return;

    const sheet = new CSSStyleSheet();
    sheet.replaceSync(formsSkin.content.querySelector('style')?.textContent ?? '');
    skinSheets.add(sheet);

    /** @type {ShadowRoot | null} */
    let observedRoot = null;
    const adopt = () => {
      const root = this.querySelector('shopify-forms-embed')?.shadowRoot;
      if (!root) return;

      if (root !== observedRoot) {
        observedRoot = root;
        observer.observe(root, { childList: true, subtree: true });
      }

      // After a submit Forms swaps the fields for its confirmation banner
      const submitted = !root.querySelector('form') && !!root.querySelector('[class*="_formBannerContainer_"]');
      if (submitted && !this.hasAttribute('data-forms-submitted')) {
        document.dispatchEvent(new CustomEvent('arvana:schedule-call:submitted'));
      }
      this.toggleAttribute('data-forms-submitted', submitted);

      const sheets = root.adoptedStyleSheets;
      if (sheets[sheets.length - 1] === sheet) return;
      root.adoptedStyleSheets = [...sheets.filter((other) => !skinSheets.has(other)), sheet];
    };

    const observer = new MutationObserver(adopt);
    observer.observe(this, { childList: true, subtree: true });
    this.#skinObserver = observer;

    adopt();
    customElements.whenDefined('shopify-forms-embed').then(adopt);
  }

  /**
   * Forms protects submissions with hCaptcha, whose challenge is a full-page layer appended to
   * <body>. A modal dialog sits above it in the top layer and makes it inert, so the visitor
   * can't solve it. While the dialog is open, check for a visible challenge and keep the dialog
   * open but non-modal until it goes away.
   */
  #watchCaptcha() {
    clearInterval(this.#captchaTimer);
    if (this.dataset.submissionProvider !== 'shopify-forms') return;

    this.#captchaTimer = setInterval(() => {
      if (!this.refs.dialog.open) {
        clearInterval(this.#captchaTimer);
        return;
      }

      const frame = document.querySelector('iframe[src*="hcaptcha"][src*="frame=challenge"]');
      let layer = frame;
      while (layer && layer.parentElement !== document.body) layer = layer.parentElement;
      const challenging = !!layer && getComputedStyle(layer).visibility === 'visible';
      this.#setModal(!challenging);
    }, 300);
  }

  /** Reopens the open dialog as modal or non-modal without running the close handling. */
  #setModal(/** @type {boolean} */ modal) {
    const { dialog } = this.refs;
    if (!dialog.open || dialog.matches(':modal') === modal) return;

    this.#switchingMode = true;
    dialog.close();
    if (modal) {
      dialog.showModal();
      this.refs.nativeHeading?.focus();
    } else {
      dialog.show();
    }
  }

  /** Native Forms has its own form internals and does not render the legacy calendar refs. */
  #configureRequiredRefs() {
    this.requiredRefs =
      this.dataset.submissionProvider === 'shopify-forms'
        ? ['dialog', 'nativeHeading']
        : [
            'dialog', 'form', 'steps', 'success', 'detailFields', 'dateInput', 'timeInput',
            'monthLabel', 'dateLabel', 'prevMonth', 'nextMonth', 'weekdays', 'days', 'pickerError',
          ];
  }

  /**
   * Shows one panel and moves focus into it.
   * @param {number} index - Step index, or -1 for the confirmation panel.
   * @param {{ focusField?: boolean }} [options]
   */
  #showPanel(index, { focusField = false } = {}) {
    const { steps, success, dialog } = this.refs;
    this.#step = index;

    for (const [i, panel] of steps.entries()) panel.hidden = i !== index;
    success.hidden = index !== -1;

    const panel = index === -1 ? success : steps[index];
    const heading = panel?.querySelector('h2');
    if (heading?.id) dialog.setAttribute('aria-labelledby', heading.id);

    if (focusField && index === 0) {
      this.refs.detailFields.querySelector('input')?.focus();
    } else {
      heading?.focus();
    }
  }

  /** @param {number} delta */
  #changeMonth(delta) {
    const target = addMonths(this.#viewMonth, delta);
    const { min, max } = this.#range();
    if (target > max || addMonths(target, 1) <= min) return;

    this.#viewMonth = target;
    this.#focusDate = null;
    this.#renderMonth();
  }

  /** @param {Date} date */
  #selectDate(date) {
    this.#selectedDate = date;
    this.#focusDate = date;
    this.#viewMonth = startOfMonth(date);
    this.#renderMonth();

    const long = new Intl.DateTimeFormat(locale(), { dateStyle: 'long' }).format(date);
    const full = new Intl.DateTimeFormat(locale(), {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);

    this.refs.dateLabel.textContent = long;
    this.refs.dateInput.value = full;
    this.refs.pickerError.hidden = true;
    this.#updateSlots();
  }

  /** Enables time slots once a date is chosen; on the current day, earlier times stay disabled. */
  #updateSlots() {
    const slots = this.refs.slots ?? [];
    const selected = this.#selectedDate;
    const now = new Date();
    const isToday = selected ? sameDay(selected, now) : false;

    for (const slot of slots) {
      let disabled = !selected;
      if (selected && isToday) {
        const minutes = parseTimeOfDay(slot.value);
        disabled = minutes !== null && minutes <= now.getHours() * 60 + now.getMinutes() + 60;
      }

      slot.disabled = disabled;
      if (disabled && slot.checked) {
        slot.checked = false;
        this.refs.timeInput.value = '';
      }
    }
  }

  #renderWeekdays() {
    const narrow = new Intl.DateTimeFormat(locale(), { weekday: 'narrow' });
    const long = new Intl.DateTimeFormat(locale(), { weekday: 'long' });
    // 7 Jan 2024 was a Sunday; the grid starts on Sunday as in Figma
    const sunday = new Date(2024, 0, 7);
    const row = document.createElement('tr');

    for (let i = 0; i < 7; i++) {
      const day = addDays(sunday, i);
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.abbr = long.format(day);
      cell.textContent = narrow.format(day);
      row.append(cell);
    }

    this.refs.weekdays.replaceChildren(row);
  }

  #renderMonth() {
    const { days, monthLabel, prevMonth, nextMonth } = this.refs;
    const { min, max } = this.#range();
    const view = this.#viewMonth;
    const month = view.getMonth();
    const labelFormat = new Intl.DateTimeFormat(locale(), {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    monthLabel.textContent = new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric' }).format(view);
    prevMonth.disabled = view <= startOfMonth(min);
    nextMonth.disabled = addMonths(view, 1) > max;

    // Roving tab stop: the focused day, else the selected day, else the first available day
    let tabStop = [this.#focusDate, this.#selectedDate].find((date) => date && sameMonth(date, view)) ?? null;
    if (!tabStop || !this.#isAvailable(tabStop)) tabStop = this.#firstAvailableDate(view) ?? tabStop;

    const start = addDays(view, -view.getDay());
    const daysInMonth = new Date(view.getFullYear(), month + 1, 0).getDate();
    const weeks = Math.ceil((view.getDay() + daysInMonth) / 7);
    const rows = [];

    for (let week = 0; week < weeks; week++) {
      const row = document.createElement('tr');

      for (let weekday = 0; weekday < 7; weekday++) {
        const date = addDays(start, week * 7 + weekday);
        const cell = document.createElement('td');

        if (date.getMonth() !== month) {
          // Neighbouring months are shown muted, as in Figma, but are not interactive
          cell.textContent = String(date.getDate());
          cell.setAttribute('aria-hidden', 'true');
        } else {
          const button = document.createElement('button');
          const available = this.#isAvailable(date);
          const selected = this.#selectedDate ? sameDay(date, this.#selectedDate) : false;

          button.type = 'button';
          button.className = 'arvana-schedule__day';
          button.dataset.date = toIsoDate(date);
          button.textContent = String(date.getDate());
          button.setAttribute('aria-label', labelFormat.format(date));
          button.setAttribute('aria-pressed', String(selected));
          button.tabIndex = tabStop && sameDay(date, tabStop) ? 0 : -1;
          if (!available) button.setAttribute('aria-disabled', 'true');

          cell.append(button);
        }

        row.append(cell);
      }

      rows.push(row);
    }

    days.replaceChildren(...rows);
  }

  /** @param {Date} date */
  #focusDay(date) {
    const button = this.refs.days.querySelector(`[data-date="${toIsoDate(date)}"]`);
    if (button instanceof HTMLElement) button.focus();
  }

  /**
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #dayButtonFrom(event) {
    const origin = event.composedPath()[0];
    if (!(origin instanceof Element)) return null;

    const button = origin.closest('.arvana-schedule__day');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /** Bookable window from the settings. */
  #range() {
    const lead = Number(this.dataset.leadDays ?? 1) || 0;
    const ahead = Number(this.dataset.maxDays ?? 60) || 60;
    const base = today();
    return { min: addDays(base, lead), max: addDays(base, ahead) };
  }

  /** Weekdays (0 = Sunday) that can be booked; Monday to Friday when none are set. */
  #availableWeekdays() {
    const days = (this.dataset.availableDays ?? '')
      .split(',')
      .filter((value) => value !== '')
      .map(Number);
    return days.length ? days : [1, 2, 3, 4, 5];
  }

  /** @param {Date} date */
  #isAvailable(date) {
    const { min, max } = this.#range();
    return date >= min && date <= max && this.#availableWeekdays().includes(date.getDay());
  }

  /**
   * First bookable day, optionally within one month.
   * @param {Date} [month]
   * @returns {Date | null}
   */
  #firstAvailableDate(month) {
    const { min, max } = this.#range();
    let date = month && startOfMonth(month) > min ? startOfMonth(month) : min;

    while (date <= max) {
      if (month && !sameMonth(date, month)) return null;
      if (this.#isAvailable(date)) return date;
      date = addDays(date, 1);
    }

    return null;
  }

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const url = new URL(link.href, window.location.href);
    if (url.hash !== TRIGGER_HASH) return;

    event.preventDefault();
    this.open();
  };

  #handleHashChange = () => {
    if (window.location.hash === TRIGGER_HASH) this.open();
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    if (event.target !== this.refs.form) return;

    // Enter in a step 1 field submits implicitly; treat it as "Continue"
    if (this.#step === 0) {
      event.preventDefault();
      this.next();
      return;
    }

    if (!this.refs.dateInput.value || !this.refs.timeInput.value) {
      event.preventDefault();
      this.refs.pickerError.hidden = false;

      const firstSlot = (this.refs.slots ?? []).find((slot) => !slot.disabled);
      if (this.refs.dateInput.value && firstSlot) {
        firstSlot.focus();
      } else {
        this.#focusDay(this.#focusDate ?? this.#firstAvailableDate() ?? today());
      }
    }
  };

  /** Resets the dialog after it closes, and drops the trigger hash so it can open again. */
  #handleClose = (/** @type {Event} */ event) => {
    if (event.target !== this.refs.dialog) return;
    if (this.#switchingMode) {
      this.#switchingMode = false;
      return;
    }

    if (this.#step === -1) {
      this.refs.form.reset();
      this.refs.dateInput.value = '';
      this.refs.timeInput.value = '';
      this.#selectedDate = null;
      this.#focusDate = null;
      this.#viewMonth = startOfMonth(today());
      this.refs.dateLabel.textContent = this.refs.dateLabel.dataset.placeholder ?? '';
      this.#setup();
      this.#showPanel(0);
    }

    this.refs.openOnLoad?.remove();

    if (window.location.hash === TRIGGER_HASH) {
      const url = new URL(window.location.href);
      url.hash = '';
      history.replaceState(history.state, '', url);
    }

    if (this.#trigger instanceof HTMLElement && this.#trigger.isConnected) this.#trigger.focus();
    this.#trigger = null;
  };

  /** Clears the invalid state as soon as a field becomes valid. @param {Event} event */
  #handleInput = (event) => {
    const field = event.target;
    if (
      (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) &&
      field.hasAttribute('aria-invalid') &&
      field.checkValidity()
    ) {
      field.removeAttribute('aria-invalid');
    }
  };

  /** Theme editor: show the dialog while its section is selected. @param {Event} event */
  #handleSectionSelect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.open();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.close();
  };
}

/** @returns {string} */
function locale() {
  return document.documentElement.lang || 'en';
}

/** @returns {Date} Today at local midnight. */
function today() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

/** @param {Date} date */
function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * @param {Date} date
 * @param {number} days
 */
function addDays(date, days) {
  // Calendar arithmetic (not milliseconds) so daylight saving changes never skip a day
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * Same day of month in another month, clamped to that month's length.
 * @param {Date} date
 * @param {number} months
 */
function addMonths(date, months) {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const length = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), length));
  return target;
}

/**
 * @param {Date} a
 * @param {Date} b
 */
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * @param {Date} a
 * @param {Date} b
 */
function sameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/** @param {Date} date */
function toIsoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * @param {string} value - YYYY-MM-DD
 * @returns {Date | null}
 */
function parseIsoDate(value) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * Minutes after midnight for labels like "9:00 AM" or "14:30".
 * @param {string} value
 * @returns {number | null}
 */
function parseTimeOfDay(value) {
  const match = value.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i);
  if (!match) return null;

  let hours = Number(match[1]) % 12;
  const minutes = Number(match[2] ?? 0);
  const meridiem = match[3]?.toLowerCase().replace(/\./g, '');

  if (!meridiem) hours = Number(match[1]);
  else if (meridiem === 'pm') hours += 12;

  return hours * 60 + minutes;
}

if (!customElements.get('arvana-schedule-call-component')) {
  customElements.define('arvana-schedule-call-component', ArvanaScheduleCallComponent);
}
